import React, { useEffect, useState } from 'react';
import { useFinance } from '../context/FinanceContext';
import { PlusCircle, Trash2, Edit2 } from 'lucide-react';
import Modal from '../components/Modal';

// A pure-CSS pushpin rendered as SVG — no extra dependencies needed
function Pushpin({ color = '#e74c3c' }) {
  return (
    <svg
      width="28"
      height="36"
      viewBox="0 0 28 36"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }}
    >
      {/* Pin head (circle) */}
      <circle cx="14" cy="11" r="11" fill={color} />
      {/* Shiny highlight on head */}
      <ellipse cx="10" cy="7" rx="4" ry="3" fill="rgba(255,255,255,0.35)" />
      {/* Pin needle */}
      <path d="M14 20 L14 36" stroke="#6b5c4e" strokeWidth="2.5" strokeLinecap="round" />
      {/* Needle tip shadow */}
      <ellipse cx="14" cy="36" rx="2" ry="1" fill="rgba(0,0,0,0.2)" />
    </svg>
  );
}

// Each card gets a slightly different rotation for a natural "pinned" look
const rotations = [-2, 1.5, -1, 2.5, -0.8, 1.2, -2.2, 0.6, -1.5, 2];

const PIN_COLORS = {
  loan: '#3b82f6',   // blue
  plan: '#a855f7',   // purple
  debt: '#f43f5e',   // rose/red
};

export default function StickyBoard() {
  const { stickyItems, stickyLoading, fetchStickyItems, addStickyItem, deleteStickyItem, updateStickyItem } = useFinance();
  const [activeTab, setActiveTab] = useState('loan');

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', amount: '', notes: '' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchStickyItems();
  }, [fetchStickyItems]);

  const handleOpenForm = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setFormData({ name: item.name, amount: item.amount, notes: item.notes || '' });
    } else {
      setEditingId(null);
      setFormData({ name: '', amount: '', notes: '' });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateStickyItem(editingId, { ...formData, type: activeTab });
      } else {
        await addStickyItem({ ...formData, type: activeTab });
      }
      setShowForm(false);
      setFormData({ name: '', amount: '', notes: '' });
      setEditingId(null);
    } catch (err) {
      alert("Error: " + (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      await deleteStickyItem(id);
    }
  };

  const tabs = [
    { id: 'loan', label: 'Loans (Owed to Me)' },
    { id: 'plan', label: 'Future Plans' },
    { id: 'debt', label: 'Deferred Debts' }
  ];

  const filteredItems = stickyItems.filter(item => item.type === activeTab);

  if (stickyLoading) {
    return <div className="p-8 text-center text-gray-500">Loading Sticky Board...</div>;
  }

  const totals = {
    loan: stickyItems.filter(i => i.type === 'loan').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0),
    plan: stickyItems.filter(i => i.type === 'plan').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0),
    debt: stickyItems.filter(i => i.type === 'debt').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0),
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-4 sm:p-8 rounded-3xl shadow-sm border border-gray-200 mt-4 md:mt-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Sticky Board</h1>
        <button
          onClick={() => handleOpenForm()}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          <PlusCircle className="w-4 h-4" />
          <span className="hidden sm:inline">Add Item</span>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
        <div className="bg-blue-300 border border-blue-400 p-2 sm:p-4 rounded-2xl sm:rounded-3xl flex flex-col justify-center">
          <h3 className="text-[9px] sm:text-xs text-gray-800 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 leading-tight">Total Owed To Me</h3>
          <p className="text-sm sm:text-xl font-bold text-gray-900 truncate">K{totals.loan.toLocaleString()}</p>
        </div>
        <div className="bg-purple-300 border border-purple-400 p-2 sm:p-4 rounded-2xl sm:rounded-3xl flex flex-col justify-center">
          <h3 className="text-[9px] sm:text-xs text-gray-800 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 leading-tight">Total Future Plans</h3>
          <p className="text-sm sm:text-xl font-bold text-gray-900 truncate">K{totals.plan.toLocaleString()}</p>
        </div>
        <div className="bg-rose-300 border border-rose-400 p-2 sm:p-4 rounded-2xl sm:rounded-3xl flex flex-col justify-center">
          <h3 className="text-[9px] sm:text-xs text-gray-800 font-bold uppercase tracking-wider mb-0.5 sm:mb-1 leading-tight">Total Deferred Debts</h3>
          <p className="text-sm sm:text-xl font-bold text-gray-900 truncate">K{totals.debt.toLocaleString()}</p>
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto hide-scrollbar">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Modal 
        isOpen={showForm} 
        onClose={() => setShowForm(false)}
        title={editingId ? 'Edit Item' : `New ${tabs.find(t => t.id === activeTab).label}`}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input
                required
                type="text"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                className="w-full border border-gray-300 p-2 rounded-md"
                placeholder="e.g. Vacation, John Doe, New TV"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 font-medium">K</span>
                <input
                  required
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={e => setFormData({...formData, amount: e.target.value})}
                  className="w-full border border-gray-300 p-2 pl-8 rounded-md"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-700 mb-1">Notes (Optional)</label>
              <input
                type="text"
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                className="w-full border border-gray-300 p-2 rounded-md"
                placeholder="Any additional details"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 mt-4">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-md hover:bg-gray-100 font-medium">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">Save</button>
          </div>
        </form>
      </Modal>

      {filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">No items found in this category.</p>
          <button onClick={() => handleOpenForm()} className="mt-2 text-blue-600 hover:underline">Click here to add one</button>
        </div>
      ) : (
        /* Cork-board style container */
        <div
          className="rounded-3xl p-6"
          style={{ background: 'repeating-linear-gradient(45deg, #c8a97a 0px, #c8a97a 1px, #b8956a 1px, #d4aa80 12px)' }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
            {filteredItems.map((item, index) => {
              let bgColor = '#fde047'; // yellow sticky default
              let borderColor = '#eab308';
              let shadowColor = 'rgba(202,138,4,0.3)';
              if (item.type === 'loan') {
                bgColor = '#93c5fd';
                borderColor = '#3b82f6';
                shadowColor = 'rgba(59,130,246,0.25)';
              }
              if (item.type === 'plan') {
                bgColor = '#d8b4fe';
                borderColor = '#a855f7';
                shadowColor = 'rgba(168,85,247,0.25)';
              }
              if (item.type === 'debt') {
                bgColor = '#fda4af';
                borderColor = '#f43f5e';
                shadowColor = 'rgba(244,63,94,0.25)';
              }

              const rotation = rotations[index % rotations.length];
              const pinColor = PIN_COLORS[item.type] || '#e74c3c';

              return (
                <div
                  key={item.id}
                  className="relative group"
                  style={{ transform: `rotate(${rotation}deg)`, transition: 'transform 0.2s ease', fontFamily: "'Kalam', cursive" }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'rotate(0deg) scale(1.04)'}
                  onMouseLeave={e => e.currentTarget.style.transform = `rotate(${rotation}deg) scale(1)`}
                >
                  {/* Pushpin — centered at the top of the card */}
                  <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none scale-75 sm:scale-90">
                    <Pushpin color={pinColor} />
                  </div>

                  {/* The sticky note card */}
                  <div
                    className="pt-4 p-3 sm:pt-5 sm:p-4 rounded-xl sm:rounded-2xl border relative flex flex-col justify-between"
                    style={{
                      backgroundColor: bgColor,
                      borderColor: borderColor,
                      boxShadow: `2px 3px 10px ${shadowColor}, 0 1px 3px rgba(0,0,0,0.15)`,
                      minHeight: '90px',
                    }}
                  >
                    {/* Edit / Delete buttons — appear on hover */}
                    <div className="absolute top-2 right-2 sm:top-2 sm:right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-20">
                      <button
                        onClick={() => handleOpenForm(item)}
                        className="p-1 text-gray-800 hover:text-blue-800 bg-white/90 rounded-md hover:bg-white shadow-sm"
                      >
                        <Edit2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1 text-gray-800 hover:text-red-800 bg-white/90 rounded-md hover:bg-white shadow-sm"
                      >
                        <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      </button>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 text-sm sm:text-base pr-6 sm:pr-10 leading-snug break-words">{item.name}</h3>
                      <p className="text-base sm:text-lg font-extrabold text-gray-900 mt-0.5 mb-1 sm:mb-1.5 break-all">
                        K{parseFloat(item.amount).toLocaleString()}
                      </p>
                      {item.notes && (
                        <p className="text-[11px] sm:text-[13px] text-gray-800 line-clamp-3 leading-tight break-words">{item.notes}</p>
                      )}
                    </div>
                    <div className="text-[9px] sm:text-[11px] text-gray-700 mt-2 sm:mt-3 font-medium tracking-wide border-t border-black/20 pt-1.5 sm:pt-2">
                      📅 {new Date(item.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
